import React, { type FormEvent, useCallback, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { type OAuth2App, useUpdateOAuth2AppMutation } from '../../../store/auth/auth'
import { addToast } from '../../../store/toast/toastSlice'
import { useListOAuth2ScopesQuery } from '../../../store/permissions/permissions'
import { ScopesFormFields } from '../components/ScopesFormFields'
import { RequestChangeModal } from './RequestChangeModal'

interface ManageAppScopesTabProps {
  oAuth2App: OAuth2App
}

export const ManageAppScopesTab: React.FC<ManageAppScopesTabProps> = ({ oAuth2App }) => {
  const isExternal = oAuth2App.type === 'external'

  if (isExternal) {
    return <ExternalAppScopesView oAuth2App={oAuth2App} />
  }

  return <EditableAppScopesView oAuth2App={oAuth2App} />
}

const ExternalAppScopesView: React.FC<ManageAppScopesTabProps> = ({ oAuth2App }) => {
  const [showModal, setShowModal] = useState<boolean>(false)
  const selectedScopes = oAuth2App.scopes
  const { data: scopesResponse } = useListOAuth2ScopesQuery()
  const totalScopes = scopesResponse?.data?.length ?? 0

  return (
    <div>
      <div className='d-flex justify-content-between align-items-start mb-4'>
        <div>
          <h5 className='fw-bold mb-1'>App scopes</h5>
          <p className='text-muted mb-0'>
            {selectedScopes.length}/{totalScopes} scopes selected.
          </p>
        </div>
        <Button variant='darkGhost' className='d-flex align-items-center gap-2' onClick={() => setShowModal(true)}>
          <i className='bi bi-pencil-square' />
          Edit
        </Button>
      </div>

      <ScopesFormFields selectedScopes={selectedScopes} readOnly />
      <RequestChangeModal show={showModal} subject='scopes' onHide={() => setShowModal(false)} />
    </div>
  )
}

const EditableAppScopesView: React.FC<ManageAppScopesTabProps> = ({ oAuth2App }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const [updateApp, { isLoading: isSaving }] = useUpdateOAuth2AppMutation()
  const { data: scopesResponse } = useListOAuth2ScopesQuery()
  const totalScopes = scopesResponse?.data?.length ?? 0

  const [selectedScopes, setSelectedScopes] = useState<string[]>(oAuth2App.scopes)
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false)

  const handleToggle = useCallback((scope: string) => {
    setSelectedScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]))
    setSaveSuccess(false)
  }, [])

  const hasChanges = useMemo(() => {
    const original = oAuth2App.scopes
    return original.length !== selectedScopes.length || original.some((s) => !selectedScopes.includes(s))
  }, [oAuth2App.scopes, selectedScopes])

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setSaveSuccess(false)

      try {
        await updateApp({
          accountId,
          clientId: oAuth2App.clientId,
          payload: { scopes: selectedScopes },
        }).unwrap()

        setSaveSuccess(true)
      } catch {
        dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to save changes. Please try again.'] }))
      }
    },
    [accountId, oAuth2App.clientId, selectedScopes, updateApp, dispatch],
  )

  const handleDiscard = useCallback(() => {
    setSelectedScopes(oAuth2App.scopes ?? [])
    setSaveSuccess(false)
  }, [oAuth2App.scopes])

  return (
    <Form onSubmit={handleSubmit}>
      <h5 className='fw-bold mb-1'>App scopes</h5>
      <p className='text-muted mb-4'>
        Scopes define what data your app can access. {selectedScopes.length}/{totalScopes} scopes selected.
      </p>

      <ScopesFormFields selectedScopes={selectedScopes} onToggle={handleToggle} />

      {saveSuccess ? <div className='text-success small mb-3'>Scopes saved successfully.</div> : null}

      <div className='d-flex justify-content-end gap-3 mt-4'>
        <Button variant='secondary' className='fw-bold' onClick={handleDiscard} disabled={!hasChanges || isSaving}>
          Discard
        </Button>
        <Button type='submit' variant='primary' className='fw-bold' disabled={!hasChanges || isSaving} loading={isSaving}>
          Save changes
        </Button>
      </div>
    </Form>
  )
}
